// Helper murni untuk fitur Popup Builder (mig 194).
//
// Sengaja BEBAS dari Supabase/React supaya gampang diuji (lihat
// src/lib/__tests__/popups.test.ts). Data-layer ada di
// src/lib/supabase/popups.ts; UI admin di
// src/components/admin/AdminPopups.tsx; tampilan storefront di
// src/components/PopupDisplay.tsx (dipasang global via Providers).
//
// Popup = kartu modal yang muncul di halaman storefront tertentu, berisi
// gambar opsional + blok konten terurut (teks berformat / tombol link),
// dengan kondisi audiens (semua / tamu / sudah login / grup reseller
// tertentu) dan frekuensi tampil (selalu / sekali per sesi / sekali per hari).

// ── Tipe ────────────────────────────────────────────────────────────────────

export type PopupTextSize = "small" | "normal" | "large";
export type PopupAlign = "left" | "center" | "right";

export type PopupTextBlock = {
  type: "text";
  text: string;
  size: PopupTextSize;
  align: PopupAlign;
  /** Warna teks hex, mis. "#1f2937". */
  color: string;
  bold: boolean;
};

export type PopupButtonBlock = {
  type: "button";
  label: string;
  /** Tujuan link: path internal ("/orders") atau URL http(s) eksternal. */
  url: string;
  align: PopupAlign;
  /** Warna latar tombol hex. */
  color: string;
  /** Warna teks tombol hex. */
  textColor: string;
};

export type PopupCountdownBlock = {
  type: "countdown";
  /** Target waktu (ISO); saat tercapai → tampilkan expiredText. */
  target: string;
  /** Teks kecil di atas angka (mis. "PO tutup dalam"). Boleh kosong. */
  label: string;
  /** Teks saat waktu habis (mis. "PO sudah ditutup"). */
  expiredText: string;
  align: PopupAlign;
  color: string;
};

export type PopupBlock =
  | PopupTextBlock
  | PopupButtonBlock
  | PopupCountdownBlock;

export type PopupAudience = "all" | "guest" | "logged_in" | "groups";
export type PopupFrequency = "always" | "once_per_session" | "once_per_day";

export type Popup = {
  id: string;
  name: string;
  isActive: boolean;
  imageUrl: string | null;
  /** Tujuan bila gambar diklik (path internal/URL); null = tak diklik. */
  imageLink: string | null;
  /** Jeda tampil (detik) setelah halaman dibuka; 0 = seketika. */
  delaySeconds: number;
  blocks: PopupBlock[];
  audience: PopupAudience;
  /** Id grup reseller (dipakai bila audience = 'groups'). */
  groupIds: string[];
  /** Kunci halaman target (lihat POPUP_PAGES); KOSONG = semua halaman. */
  pages: string[];
  frequency: PopupFrequency;
  /** Periode waktu tampil (ISO); null = tanpa batas. */
  startsAt: string | null;
  endsAt: string | null;
  sortOrder: number;
  createdAt: string | null;
};

export type PopupDraft = {
  name: string;
  isActive: boolean;
  imageUrl: string | null;
  imageLink: string | null;
  delaySeconds: number;
  blocks: PopupBlock[];
  audience: PopupAudience;
  groupIds: string[];
  pages: string[];
  frequency: PopupFrequency;
  startsAt: string | null;
  endsAt: string | null;
};

// ── Halaman storefront yang bisa ditarget ───────────────────────────────────

/**
 * KUNCI = SEGMEN ROUTE-nya, verbatim (`/notifications` → "notifications").
 * Nilainya tersimpan permanen di kolom `popups.pages`, jadi saat men-debug lewat
 * SQL `pages = {notifications}` harus bisa langsung di-grep ke `src/app/`.
 *
 * Aturan itu pernah dilanggar sekali: kunci **"checkout" menunjuk route yang
 * tidak pernah ada** (checkout = state di dalam `CartCheckout`, dirender inline
 * di `/keranjang`), sehingga checkbox-nya no-op senyap sejak lahir — popup
 * tersimpan rapi lalu tak pernah tampil, tanpa error. **DICABUT 2026-08-14 atas
 * keputusan pemilik.**
 *
 * Pencabutannya **nol perubahan perilaku storefront**, dan itu hasil pemeriksaan
 * bukan asumsi: PROD tak punya satu pun popup yang menyebut "checkout"
 * (diverifikasi sebelum mencabut), dan kuncinya sengaja DICABUT — bukan
 * diarahkan ke `/keranjang`. Mengarahkannya justru yang akan mengubah keadaan
 * hidup: popup ber-`pages=[]` ("semua halaman") langsung mulai menyembul di
 * keranjang pembeli. Kalau kelak popup memang diinginkan di sana, tambahkan
 * kunci `keranjang` beserta aturan prefix-nya — jangan menghidupkan kembali
 * "checkout", karena nilainya sudah telanjur bisa tersimpan di kolom `pages`
 * milik toko mana pun dan maknanya akan berbeda dari dulu.
 *
 * Baris `pages` lama yang masih memuat "checkout" (kalau ada di toko yang tak
 * terlihat saat pencabutan) tetap AMAN: tak ada halaman yang menghasilkan kunci
 * itu, jadi ia diabaikan persis seperti sebelumnya. Yang HARAM adalah
 * membersihkannya lewat SQL — membuang satu-satunya entri `pages` mengubahnya
 * jadi `[]` yang berarti "semua halaman", dan popup yang selama ini tak pernah
 * tampil akan muncul di SELURUH storefront.
 *
 * Invarian ini dikunci `popups.test.ts` → `KNOWN_DEAD_PAGE_KEYS`, yang menolak
 * kunci mati BARU dengan memeriksa keberadaan route-nya di `src/app/`. Daftar
 * itu kini KOSONG dan hanya boleh tetap kosong. Jangan menambah kunci tanpa
 * route.
 */
export const POPUP_PAGES: readonly { key: string; label: string }[] = [
  { key: "home", label: "Beranda" },
  { key: "login", label: "Halaman Login" },
  { key: "daftar", label: "Halaman Daftar" },
  { key: "product", label: "Detail Produk" },
  { key: "cari", label: "Hasil Pencarian" },
  { key: "kategori", label: "Halaman Kategori" },
  { key: "merek", label: "Halaman Merek" },
  // Urutan 4 entri berikut mencermin menu pembeli (Pesanan · Tagihan ·
  // Notifikasi · Akun Saya) supaya admin memilih halaman dengan urutan yang
  // sudah dikenalnya. Notifikasi sejak 2026-09-24 = lonceng di header (sebelah
  // keranjang), tiga lainnya tab BottomNav.
  { key: "orders", label: "Pesanan" },
  { key: "tagihan", label: "Tagihan" },
  { key: "notifications", label: "Notifikasi" },
  { key: "account", label: "Akun Saya" },
  { key: "saldo", label: "Saldo" },
  { key: "poin", label: "Poin" },
  { key: "tutorial", label: "Tutorial" },
] as const;

export function popupPageLabel(key: string): string {
  return POPUP_PAGES.find((p) => p.key === key)?.label ?? key;
}

/**
 * Petakan pathname storefront → kunci halaman popup, atau `null` bila halaman
 * itu tidak pernah menampilkan popup (mis. /admin, /maintenance, API).
 * Prefix paling spesifik menang (/orders/tagihan → "tagihan", /orders →
 * "orders").
 */
export function resolvePopupPageKey(pathname: string): string | null {
  const path = (pathname || "/").split("?")[0].split("#")[0];
  if (path.startsWith("/admin")) return null;
  if (path === "/" || path === "") return "home";

  const rules: readonly { prefix: string; key: string }[] = [
    { prefix: "/login", key: "login" },
    { prefix: "/daftar", key: "daftar" },
    { prefix: "/product", key: "product" },
    { prefix: "/cari", key: "cari" },
    { prefix: "/kategori", key: "kategori" },
    { prefix: "/merek", key: "merek" },
    // Lebih spesifik dulu: detail nota & pembayaran di bawah /orders.
    { prefix: "/orders/tagihan", key: "tagihan" },
    { prefix: "/orders", key: "orders" },
    { prefix: "/tagihan", key: "tagihan" },
    { prefix: "/pembayaran", key: "tagihan" },
    // Halaman notifikasi STOREFRONT. Guard `/admin` di atas dievaluasi lebih
    // dulu, jadi panel admin `/admin/notifications` TETAP null — popup toko
    // tidak boleh menyembul di panel pengelola.
    { prefix: "/notifications", key: "notifications" },
    { prefix: "/account", key: "account" },
    { prefix: "/saldo", key: "saldo" },
    { prefix: "/poin", key: "poin" },
    { prefix: "/tutorial", key: "tutorial" },
  ];
  for (const r of rules) {
    if (path === r.prefix || path.startsWith(`${r.prefix}/`)) return r.key;
  }
  return null;
}

// ── Blok konten ─────────────────────────────────────────────────────────────

export const DEFAULT_TEXT_COLOR = "#1f2937";
/**
 * Warna tombol/countdown bawaan untuk popup BARU. Ini hanya nilai awal yang
 * langsung bisa diganti admin lewat pemilih warna — nilainya tersimpan per-popup
 * di DB sebagai hex, jadi TIDAK bisa memakai `rgb(var(--theme-primary))`.
 * Pemanggil yang tahu warna tema toko meneruskannya lewat argumen `accent`
 * (lihat `emptyButtonBlock`); konstanta ini tinggal jaring pengaman.
 */
export const DEFAULT_BUTTON_COLOR = "#EE4D2D";
export const DEFAULT_BUTTON_TEXT_COLOR = "#ffffff";

/** Ukuran font (px) per pilihan ukuran teks — dipakai preview & storefront. */
export const POPUP_TEXT_SIZE_PX: Record<PopupTextSize, number> = {
  small: 12,
  normal: 14,
  large: 20,
};

export const POPUP_TEXT_SIZE_OPTIONS: readonly {
  value: PopupTextSize;
  label: string;
}[] = [
  { value: "small", label: "Kecil" },
  { value: "normal", label: "Normal" },
  { value: "large", label: "Besar" },
] as const;

export const POPUP_ALIGN_OPTIONS: readonly {
  value: PopupAlign;
  label: string;
}[] = [
  { value: "left", label: "Kiri" },
  { value: "center", label: "Tengah" },
  { value: "right", label: "Kanan" },
] as const;

export function emptyTextBlock(): PopupTextBlock {
  return {
    type: "text",
    text: "",
    size: "normal",
    align: "center",
    color: DEFAULT_TEXT_COLOR,
    bold: false,
  };
}

/**
 * Blok tombol baru. `accent` = warna tema toko dalam hex (opsional) supaya popup
 * baru lahir se-warna dengan tokonya, bukan oranye bawaan. Nilai tak valid /
 * tidak diisi → `DEFAULT_BUTTON_COLOR`.
 */
export function emptyButtonBlock(accent?: string | null): PopupButtonBlock {
  return {
    type: "button",
    label: "",
    url: "",
    align: "center",
    color: asColor(accent, DEFAULT_BUTTON_COLOR),
    textColor: DEFAULT_BUTTON_TEXT_COLOR,
  };
}

/** Blok countdown baru — `accent` sama artinya dengan `emptyButtonBlock`. */
export function emptyCountdownBlock(accent?: string | null): PopupCountdownBlock {
  return {
    type: "countdown",
    target: "",
    label: "",
    expiredText: "Waktu habis",
    align: "center",
    color: asColor(accent, DEFAULT_BUTTON_COLOR),
  };
}

function asAlign(v: unknown): PopupAlign {
  return v === "left" || v === "right" || v === "center" ? v : "center";
}

function asSize(v: unknown): PopupTextSize {
  return v === "small" || v === "large" || v === "normal" ? v : "normal";
}

function asColor(v: unknown, fallback: string): string {
  return typeof v === "string" && /^#[0-9a-fA-F]{3,8}$/.test(v.trim())
    ? v.trim()
    : fallback;
}

/**
 * Parse defensif isi kolom jsonb `blocks` dari DB → PopupBlock[]. Entri yang
 * tak dikenal dibuang; field yang hilang diberi default aman.
 */
export function normalizePopupBlocks(raw: unknown): PopupBlock[] {
  if (!Array.isArray(raw)) return [];
  const out: PopupBlock[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const b = item as Record<string, unknown>;
    if (b.type === "text") {
      out.push({
        type: "text",
        text: typeof b.text === "string" ? b.text : "",
        size: asSize(b.size),
        align: asAlign(b.align),
        color: asColor(b.color, DEFAULT_TEXT_COLOR),
        bold: b.bold === true,
      });
    } else if (b.type === "button") {
      out.push({
        type: "button",
        label: typeof b.label === "string" ? b.label : "",
        url: typeof b.url === "string" ? b.url : "",
        align: asAlign(b.align),
        color: asColor(b.color, DEFAULT_BUTTON_COLOR),
        textColor: asColor(b.textColor, DEFAULT_BUTTON_TEXT_COLOR),
      });
    } else if (b.type === "countdown") {
      out.push({
        type: "countdown",
        target: typeof b.target === "string" ? b.target : "",
        label: typeof b.label === "string" ? b.label : "",
        expiredText:
          typeof b.expiredText === "string" ? b.expiredText : "Waktu habis",
        align: asAlign(b.align),
        color: asColor(b.color, DEFAULT_BUTTON_COLOR),
      });
    }
  }
  return out;
}

/** True bila URL tombol adalah link internal aplikasi ("/..."). */
export function isInternalPopupLink(url: string): boolean {
  const v = url.trim();
  return v.startsWith("/") && !v.startsWith("//");
}

/** Validasi URL tombol: path internal atau http(s). */
export function isValidPopupLink(url: string): boolean {
  const v = url.trim();
  if (!v) return false;
  if (isInternalPopupLink(v)) return true;
  return /^https?:\/\/.+/i.test(v);
}

// ── Pembuat link (dipakai pemilih tujuan produk/kategori/merek/halaman) ──────

/** Link ke detail produk dari slug. */
export function buildProductLink(slug: string): string {
  return `/product/${slug.trim()}`;
}

/** Link ke halaman Kategori terfilter satu kategori. */
export function buildCategoryLink(name: string): string {
  return `/kategori?cat=${encodeURIComponent(name.trim())}`;
}

/** Link ke halaman Merek terfilter satu merek. */
export function buildBrandLink(name: string): string {
  return `/merek?brand=${encodeURIComponent(name.trim())}`;
}

/**
 * Halaman landing storefront yang bisa dipilih sebagai tujuan link (subset
 * POPUP_PAGES yang punya path tetap tanpa parameter). `product` butuh konteks
 * (slug) jadi tak masuk sini — untuk produk gunakan pemilih produk.
 */
export const POPUP_LINK_PAGES: readonly { path: string; label: string }[] = [
  { path: "/", label: "Beranda" },
  { path: "/cari", label: "Hasil Pencarian" },
  { path: "/kategori", label: "Halaman Kategori" },
  { path: "/merek", label: "Halaman Merek" },
  { path: "/orders", label: "Pesanan" },
  { path: "/tagihan", label: "Tagihan" },
  { path: "/notifications", label: "Notifikasi" },
  { path: "/account", label: "Akun Saya" },
  { path: "/saldo", label: "Saldo" },
  { path: "/poin", label: "Poin" },
  { path: "/tutorial", label: "Tutorial" },
  { path: "/cari-mitra", label: "Cari Mitra" },
  { path: "/login", label: "Login" },
  { path: "/daftar", label: "Daftar" },
] as const;

// ── Hitung mundur (countdown block) ─────────────────────────────────────────

export type CountdownParts = {
  expired: boolean;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

/** Sisa waktu menuju target (ISO) dari `now`, dipecah hari/jam/menit/detik. */
export function countdownParts(
  target: string,
  now: Date = new Date()
): CountdownParts {
  const end = new Date(target);
  const ms = end.getTime() - now.getTime();
  if (Number.isNaN(end.getTime()) || ms <= 0) {
    return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
  }
  const totalSec = Math.floor(ms / 1000);
  return {
    expired: false,
    days: Math.floor(totalSec / 86400),
    hours: Math.floor((totalSec % 86400) / 3600),
    minutes: Math.floor((totalSec % 3600) / 60),
    seconds: totalSec % 60,
  };
}

/** Format sisa waktu jadi teks Indonesia ringkas ("2 hari 3 jam 4 menit 5 detik"). */
export function formatCountdown(parts: CountdownParts): string {
  const seg: string[] = [];
  if (parts.days > 0) seg.push(`${parts.days} hari`);
  if (parts.days > 0 || parts.hours > 0) seg.push(`${parts.hours} jam`);
  seg.push(`${parts.minutes} menit`);
  seg.push(`${parts.seconds} detik`);
  return seg.join(" ");
}

// ── Kecocokan konteks (halaman + audiens) ───────────────────────────────────

export type PopupContext = {
  /** Kunci halaman saat ini (hasil resolvePopupPageKey), null = bukan target. */
  pageKey: string | null;
  isLoggedIn: boolean;
  /** Id grup reseller pembeli login (null bila tamu / tanpa grup). */
  groupId: string | null;
  /** Waktu evaluasi (default: sekarang) — untuk cek periode tampil. */
  now?: Date;
};

/** True bila `now` berada dalam periode tampil popup (batas null = bebas). */
export function isPopupInSchedule(
  popup: Pick<Popup, "startsAt" | "endsAt">,
  now: Date = new Date()
): boolean {
  if (popup.startsAt) {
    const start = new Date(popup.startsAt);
    if (!Number.isNaN(start.getTime()) && now < start) return false;
  }
  if (popup.endsAt) {
    const end = new Date(popup.endsAt);
    if (!Number.isNaN(end.getTime()) && now >= end) return false;
  }
  return true;
}

/**
 * True bila popup boleh tampil pada konteks (halaman + status login/grup +
 * periode waktu tampil).
 */
export function popupMatchesContext(popup: Popup, ctx: PopupContext): boolean {
  if (!popup.isActive) return false;
  if (!ctx.pageKey) return false;
  if (!isPopupInSchedule(popup, ctx.now ?? new Date())) return false;
  if (popup.pages.length > 0 && !popup.pages.includes(ctx.pageKey)) {
    return false;
  }
  switch (popup.audience) {
    case "all":
      return true;
    case "guest":
      return !ctx.isLoggedIn;
    case "logged_in":
      return ctx.isLoggedIn;
    case "groups":
      return (
        ctx.isLoggedIn &&
        !!ctx.groupId &&
        popup.groupIds.includes(ctx.groupId)
      );
    default:
      return false;
  }
}

// ── Frekuensi tampil ────────────────────────────────────────────────────────

/** Key penanda "sudah tampil" di local/sessionStorage. */
export function popupStorageKey(popupId: string): string {
  return `galea-popup-shown-${popupId}`;
}

/** True bila dua ISO timestamp jatuh di hari lokal yang sama. */
function sameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * True bila popup harus DISEMBUNYIKAN karena aturan frekuensinya:
 * - always           → tak pernah disuppress (muncul tiap kunjungan halaman);
 * - once_per_session → sudah tampil di sesi tab ini (`sessionShown`);
 * - once_per_day     → terakhir tampil (`lastShownAt`, ISO dari localStorage)
 *                      masih di hari lokal yang sama.
 */
export function shouldSuppressPopup(
  popup: Pick<Popup, "frequency">,
  opts: { sessionShown: boolean; lastShownAt: string | null; now?: Date }
): boolean {
  switch (popup.frequency) {
    case "always":
      return false;
    case "once_per_session":
      return opts.sessionShown;
    case "once_per_day": {
      if (!opts.lastShownAt) return false;
      const last = new Date(opts.lastShownAt);
      if (Number.isNaN(last.getTime())) return false;
      return sameLocalDay(last, opts.now ?? new Date());
    }
    default:
      return false;
  }
}

export const POPUP_FREQUENCY_OPTIONS: readonly {
  value: PopupFrequency;
  label: string;
}[] = [
  { value: "once_per_session", label: "Sekali per sesi (sampai tab ditutup)" },
  { value: "once_per_day", label: "Sekali per hari" },
  { value: "always", label: "Setiap membuka halaman" },
] as const;

export function popupFrequencyLabel(freq: PopupFrequency): string {
  return (
    POPUP_FREQUENCY_OPTIONS.find((o) => o.value === freq)?.label ?? freq
  );
}

// ── Pemilihan popup ─────────────────────────────────────────────────────────

/** Urutkan popup: sortOrder kecil dulu, lalu terbaru dulu. */
export function sortPopups(popups: Popup[]): Popup[] {
  return [...popups].sort(
    (a, b) =>
      a.sortOrder - b.sortOrder ||
      (b.createdAt ?? "").localeCompare(a.createdAt ?? "")
  );
}

/**
 * Pilih SATU popup untuk ditampilkan pada konteks saat ini: cocok halaman +
 * audiens dan tidak disuppress frekuensi (`isSuppressed` membaca storage di
 * sisi pemanggil). Kandidat diurut sortOrder → terbaru; yang pertama menang.
 */
export function pickPopup(
  popups: Popup[],
  ctx: PopupContext,
  isSuppressed: (popup: Popup) => boolean
): Popup | null {
  return (
    sortPopups(popups).find(
      (p) => popupMatchesContext(p, ctx) && !isSuppressed(p)
    ) ?? null
  );
}

// ── Draft & validasi (editor admin) ─────────────────────────────────────────

export function emptyPopupDraft(): PopupDraft {
  return {
    name: "",
    isActive: true,
    imageUrl: null,
    imageLink: null,
    delaySeconds: 0,
    blocks: [],
    audience: "all",
    groupIds: [],
    pages: [],
    frequency: "once_per_session",
    startsAt: null,
    endsAt: null,
  };
}

export function draftFromPopup(popup: Popup): PopupDraft {
  return {
    name: popup.name,
    isActive: popup.isActive,
    imageUrl: popup.imageUrl,
    imageLink: popup.imageLink,
    delaySeconds: popup.delaySeconds,
    blocks: popup.blocks.map((b) => ({ ...b })),
    audience: popup.audience,
    groupIds: [...popup.groupIds],
    pages: [...popup.pages],
    frequency: popup.frequency,
    startsAt: popup.startsAt,
    endsAt: popup.endsAt,
  };
}

/**
 * Buat draft SALINAN dari popup yang sudah ada (fitur Duplikat): sama persis
 * kecuali nama diberi akhiran " (salinan)" & dinonaktifkan supaya tak langsung
 * tayang sebelum admin memeriksanya.
 */
export function duplicatePopupDraft(popup: Popup): PopupDraft {
  return {
    ...draftFromPopup(popup),
    name: `${popup.name} (salinan)`,
    isActive: false,
  };
}

// ── Periode waktu tampil (editor admin) ─────────────────────────────────────

/**
 * ISO timestamp → nilai input `datetime-local` ("YYYY-MM-DDTHH:mm", zona
 * lokal browser). Kosong bila null/invalid.
 */
export function isoToLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

/** Nilai input `datetime-local` → ISO timestamp (null bila kosong/invalid). */
export function localInputToIso(value: string): string | null {
  const v = value.trim();
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** Ringkasan periode tampil untuk kartu daftar admin ("" bila tanpa batas). */
export function describePopupSchedule(
  popup: Pick<Popup, "startsAt" | "endsAt">,
  now: Date = new Date()
): string {
  if (!popup.startsAt && !popup.endsAt) return "";
  const fmt = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  const status = !isPopupInSchedule(popup, now)
    ? popup.startsAt && now < new Date(popup.startsAt)
      ? " (terjadwal)"
      : " (berakhir)"
    : "";
  if (popup.startsAt && popup.endsAt) {
    return `${fmt(popup.startsAt)} – ${fmt(popup.endsAt)}${status}`;
  }
  if (popup.startsAt) return `Mulai ${fmt(popup.startsAt)}${status}`;
  return `Sampai ${fmt(popup.endsAt as string)}${status}`;
}

/**
 * Validasi draft sebelum simpan. Mengembalikan pesan error (Bahasa Indonesia)
 * bila tidak valid, atau `null` bila lolos.
 */
export function validatePopupDraft(draft: PopupDraft): string | null {
  if (!draft.name.trim()) {
    return "Nama popup wajib diisi.";
  }
  const hasText = draft.blocks.some(
    (b) => b.type === "text" && b.text.trim().length > 0
  );
  const hasOther = draft.blocks.some(
    (b) => b.type === "button" || b.type === "countdown"
  );
  if (!draft.imageUrl && !hasText && !hasOther) {
    return "Popup butuh isi: unggah gambar atau tambahkan minimal satu blok teks/tombol/hitung mundur.";
  }
  if (draft.imageLink && !isValidPopupLink(draft.imageLink)) {
    return "Link gambar tidak valid — pakai path internal (mis. /cari) atau URL https://…";
  }
  for (const b of draft.blocks) {
    if (b.type === "text" && !b.text.trim()) {
      return "Ada blok teks yang masih kosong — isi teksnya atau hapus bloknya.";
    }
    if (b.type === "button") {
      if (!b.label.trim()) {
        return "Ada tombol tanpa tulisan — isi label tombolnya.";
      }
      if (!isValidPopupLink(b.url)) {
        return `Link tombol "${b.label.trim()}" tidak valid — pakai path internal (mis. /orders) atau URL https://…`;
      }
    }
    if (b.type === "countdown") {
      const t = new Date(b.target);
      if (!b.target.trim() || Number.isNaN(t.getTime())) {
        return "Blok hitung mundur butuh target waktu yang valid.";
      }
    }
  }
  if (draft.delaySeconds < 0) {
    return "Jeda tampil tidak boleh negatif.";
  }
  if (draft.audience === "groups" && draft.groupIds.length === 0) {
    return "Pilih minimal satu grup reseller untuk audiens grup tertentu.";
  }
  if (draft.startsAt && draft.endsAt) {
    const start = new Date(draft.startsAt);
    const end = new Date(draft.endsAt);
    if (
      !Number.isNaN(start.getTime()) &&
      !Number.isNaN(end.getTime()) &&
      end <= start
    ) {
      return "Waktu berakhir harus SETELAH waktu mulai tampil.";
    }
  }
  return null;
}

/** Ringkasan target halaman untuk kartu daftar admin. */
export function describePopupPages(pages: string[]): string {
  if (pages.length === 0) return "Semua halaman";
  return pages.map(popupPageLabel).join(", ");
}

/** Label audiens untuk kartu daftar admin. */
export function describePopupAudience(
  popup: Pick<Popup, "audience" | "groupIds">,
  groups: { id: string; name: string }[] = []
): string {
  switch (popup.audience) {
    case "all":
      return "Semua pengunjung";
    case "guest":
      return "Belum login (tamu)";
    case "logged_in":
      return "Sudah login";
    case "groups": {
      const names = popup.groupIds
        .map((id) => groups.find((g) => g.id === id)?.name)
        .filter((n): n is string => !!n);
      return names.length ? `Grup: ${names.join(", ")}` : "Grup tertentu";
    }
    default:
      return popup.audience;
  }
}

/**
 * Pindahkan popup pada `index` satu langkah ke atas/bawah dan kembalikan
 * daftar baru dengan `sortOrder` dinormalisasi = posisi indeksnya. Tak
 * memutasi input; bila pergerakan tak valid kembalikan daftar asal apa adanya.
 * (Pola cermin moveExpedition di expeditions.ts.)
 */
export function movePopup(
  list: Popup[],
  index: number,
  dir: "up" | "down"
): Popup[] {
  const target = dir === "up" ? index - 1 : index + 1;
  if (index < 0 || index >= list.length || target < 0 || target >= list.length) {
    return list;
  }
  const next = [...list];
  [next[index], next[target]] = [next[target], next[index]];
  return next.map((p, i) => ({ ...p, sortOrder: i }));
}

// ── Mapper baris DB ─────────────────────────────────────────────────────────

export type PopupRow = {
  id: string;
  name: string;
  is_active: boolean;
  image_url: string | null;
  image_link: string | null;
  delay_seconds: number | null;
  blocks: unknown;
  audience: string;
  group_ids: string[] | null;
  pages: string[] | null;
  frequency: string;
  starts_at: string | null;
  ends_at: string | null;
  sort_order: number | null;
  created_at: string | null;
};

export function mapPopupRow(row: PopupRow): Popup {
  const audience: PopupAudience =
    row.audience === "guest" ||
    row.audience === "logged_in" ||
    row.audience === "groups"
      ? row.audience
      : "all";
  const frequency: PopupFrequency =
    row.frequency === "always" || row.frequency === "once_per_day"
      ? row.frequency
      : "once_per_session";
  return {
    id: row.id,
    name: row.name,
    isActive: row.is_active,
    imageUrl: row.image_url,
    imageLink: row.image_link,
    delaySeconds: Math.max(0, Number(row.delay_seconds ?? 0)),
    blocks: normalizePopupBlocks(row.blocks),
    audience,
    groupIds: Array.isArray(row.group_ids) ? row.group_ids : [],
    pages: Array.isArray(row.pages) ? row.pages : [],
    frequency,
    startsAt: row.starts_at,
    endsAt: row.ends_at,
    sortOrder: Number(row.sort_order ?? 0),
    createdAt: row.created_at,
  };
}
